import { includes } from 'lodash';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { getEntityTypeFromSubtype } from '@/modules/entities/utils/getEntityTypeFromSubtype';
import {
  ClientOrganizationKind,
  EntityKind,
  EntityStage,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { NON_GIFT_RECIPIENT_ENTITY_TYPES } from '../gifting.constants';
import { TransferOption, TransferOptionType } from '../TransferOption.type';
import {
  TransferModalOptions_ClientOrganizationFragment,
  TransferModalOptions_EntityFragment,
  TransferModalOptionsQuery,
} from './graphql/TransferModalOptions.generated';
import { TransferType } from './LogNewTransferModal.types';

const ENTITY_FILTER_FUNCTIONS: {
  [key in TransferType]: (
    isCharitableDistribution: boolean
  ) => (arg: TransferModalOptions_EntityFragment) => boolean;
} = {
  [TransferType.Distribution]:
    (isCharitableDistribution) =>
    ({ kind, stage }): boolean => {
      // Filter out pre-created entities
      if (stage === EntityStage.PreCreated) {
        return false;
      }
      if (isCharitableDistribution) {
        return false;
      }
      return ![
        EntityKind.GratTrust,
        EntityKind.CrtTrust,
        EntityKind.CltTrust,
        EntityKind.InsurancePersonalAccount,
      ].includes(kind);
    },
  [TransferType.Contribution]:
    (_) =>
    (entity): boolean => {
      // Filter out pre-created entities
      if (entity.stage === EntityStage.PreCreated) {
        return false;
      }
      if (!entity.subtype.__typename) {
        return false;
      }
      const entityType = getEntityTypeFromSubtype(entity.subtype.__typename);

      return !includes(NON_GIFT_RECIPIENT_ENTITY_TYPES, entityType);
    },
};

const ORGANIZATION_FILTER_FUNCTIONS: {
  [key in TransferType]: (
    isCharitableDistribution: boolean
  ) => (arg: TransferModalOptions_ClientOrganizationFragment) => boolean;
} = {
  [TransferType.Distribution]: (isCharitableDistribution) => (organization) => {
    if (isCharitableDistribution) {
      return (
        organization.kind === ClientOrganizationKind.CharitableOrganization
      );
    }
    return true;
  },
  [TransferType.Contribution]:
    (_) =>
    ({ kind }) =>
      !!kind && ![ClientOrganizationKind.CharitableOrganization].includes(kind),
};

export function mapDataToSourceRecipientOptions(
  data: TransferModalOptionsQuery | undefined | null,
  initiatorId: string,
  transferType: TransferType,
  isCharitableDistribution: boolean,
  isInboundDistribution: boolean
): {
  transferOptions: TransferOption[];
  selectOptions: TypeaheadSelectInputOption<string>[];
} {
  if (!data) {
    return { transferOptions: [], selectOptions: [] };
  }

  const household = getNodes(data?.households)[0];
  const grantorOptions: TransferOption[] =
    (!isCharitableDistribution &&
      household?.possiblePrimaryClients.map(({ id, displayName }) => ({
        id: id,
        label: displayName,
        type: TransferOptionType.Grantor,
      }))) ||
    [];

  const entityOptions: TransferOption[] =
    getNodes(household?.entities)
      .filter(({ id }) => id !== initiatorId)
      .filter((e) => {
        let transferTypeForFilter = transferType;
        if (isInboundDistribution) {
          transferTypeForFilter = TransferType.Distribution;
        }
        const filterFunction = ENTITY_FILTER_FUNCTIONS[transferTypeForFilter](
          isCharitableDistribution
        );
        return filterFunction(e);
      })
      .map(({ id, kind, subtype: { displayName } }) => ({
        id,
        label: displayName,
        type: TransferOptionType.Entity,
        entityKind: kind,
      })) ?? [];

  const organizationOptions: TransferOption[] =
    (household?.clientOrganizations || [])
      .filter((e) => {
        let transferTypeForFilter = transferType;
        if (isInboundDistribution) {
          transferTypeForFilter = TransferType.Distribution;
        }
        const filterFunction = ORGANIZATION_FILTER_FUNCTIONS[
          transferTypeForFilter
        ](isCharitableDistribution);
        return filterFunction(e);
      })
      .map(({ id, name }) => ({
        id,
        label: name,
        type: TransferOptionType.Organization,
      })) ?? [];

  const transferOptions = [
    ...grantorOptions,
    ...entityOptions,
    ...organizationOptions,
  ];

  return {
    transferOptions,
    selectOptions: getOptions(transferOptions),
  };
}

function getOptions(
  transactionTargetOptions: TransferOption[]
): TypeaheadSelectInputOption<string>[] {
  const grantorOptions = transactionTargetOptions
    .filter(({ type }) => type === TransferOptionType.Grantor)
    .map(({ id, label }) => ({
      value: id,
      display: label,
      groupName: 'Individuals',
    }));
  const entityOptions = transactionTargetOptions
    .filter(({ type }) => type === TransferOptionType.Entity)
    .map(({ id, label }) => ({
      value: id,
      display: label,
      groupName: 'Entities',
    }));
  const organizationOptions = transactionTargetOptions
    .filter(({ type }) => type === TransferOptionType.Organization)
    .map(({ id, label }) => ({
      value: id,
      display: label,
      groupName: 'Organizations',
    }));

  return [...grantorOptions, ...organizationOptions, ...entityOptions];
}
